import {
  checkPasswordValidation,
  hasSequence,
  validateEmail,
  validatePassword,
} from '@portal/utils/validate';
import { Row, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { validateCNPJ, validateCPF, validatePhone } from 'validations-br';

import { AuthActions, OnboardingActions } from '@portal/actions';
import {
  ONBOARDING_CELLPHONE_STATUS,
  ONBOARDING_EMAIL_STATUS,
  ONBOARDING_TAX_ID_STATUS,
} from '@portal/actions/actionTypes';
import { onboarding } from '@portal/config/app.json';
import { EventType } from '@portal/enum/analytics';
import {
  DocumentType,
  DocumentUploadOrderAndType,
} from '@portal/enum/documentType';
import { PinErrors } from '@portal/enum/errors';
import { TAB_KEY, TaxIdentifierType } from '@portal/enum/onboardingType';
import { validateStatus } from '@portal/enum/validateStatus';
import {
  AccountGroupType,
  AccountStatus,
  AccountTaxIdentifierType,
  CompanyType,
  PersonType,
  RepresentorType,
  UserCredentialType,
} from '@portal/models/enumerators';
import {
  CountriesEnum,
  TermsTypes,
  UserGender,
} from '@portal/models/enumerators/onboarding';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import { removeSpecialChars, unmaskField } from '@portal/services/masks';
import { replaceAmpersandWithE } from '@portal/utils/format';
import { DateTime } from 'luxon';

import CreateAccountCorporative from '@portal/components/CreateAccountCorporative/CreateAccountCorporative';
import CreateAccountCredential from '@portal/components/CreateAccountCredential/CreateAccountCredential';
import OnboardingCadastralSituationDocument from '@portal/components/OnboardingCadastralSituationDocument/OnboardingCadastralSituationDocument';
import OnboardingDocumentPhoto from '@portal/components/OnboardingDocumentPhoto/OnboardingDocumentPhoto';
import OnboardingSocialContract from '@portal/components/OnboardingSocialContract/OnboardingSocialContract';

import { DocumentToUploadType } from '@portal/actions/onboarding';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ApprovalCompany from '@portal/components/ApprovalCompany/ApprovalCompany';
import ApprovalDevice from '@portal/components/ApprovalDevice/ApprovalDevice';
import CreateAccountUser from '@portal/components/CreateAccountUser/CreateAccountUser';
import CustomDocument from '@portal/components/CustomDocument/CustomDocument';
import Loading from '@portal/components/Loading/Loading';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import OnboardingAddress from '@portal/components/OnboardingAddress/OnboardingAddress';
import OnboardingAnnualIncome from '@portal/components/OnboardingAnnualIncome/OnboardingAnnualIncome';
import OnboardingCreatePin from '@portal/components/OnboardingCreatePin/OnBoardingCreatePin';
import OnboardingFinal from '@portal/components/OnboardingFInal/OnboardingFinal';
import OnboardingUserPhoto from '@portal/components/OnboardingUserPhoto/OnboardingUserPhoto';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import Recommendation from '@portal/components/Recommendation/Recommendation';
import TermsAndPrivacy from '@portal/components/TermsAndPrivacy/TermsAndPrivacy';
import { ONBOARDING_MAXLENGTH_CNAE } from '@portal/config/env';
import useReduxState from '@portal/hooks/useReduxState';
import NavigationService from '@portal/services/navigation';
import * as MessageService from '~/services/message';
import * as StorageService from '~/services/storage';

import IdwallSDK from '@portal/components/IdwallSDK/IdwallSDK';
import OnboardingBusinessAddressDocument from '@portal/components/OnboardingBusinessAddressDocument/OnboardingBusinessAddressDocument';
import OnboardingCAF from '@portal/components/OnboardingCaf/OnboardingCaf';
import OnboardingCbo from '@portal/components/OnboardingCbo/OnboardingCbo';
import createOccupationStructure from '@portal/components/OnboardingCbo/createOccupationStructure';
import OnboardingFileOfMembersAndAdministrator from '@portal/components/OnboardingFileOfMembersAndAdministrator/OnboardingFileOfMembersAndAdministrator';
import OnboardingFinancialStatements from '@portal/components/OnboardingFinancialStatements/OnboardingFinancialStatements';
import OnboardingHomeAddressDocument from '@portal/components/OnboardingHomeAddressDocument/OnboardingHomeAddressDocument';
import OnboardingMinutesOfElection from '@portal/components/OnboardingMinutesOfElection/OnboardingMinutesOfElection';

import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import OnboardingCompanyStatute from '@portal/components/OnboardingCompanyStatute/OnboardingCompanyStatute';
import OnboardingDeclarationBeneficiaries from '@portal/components/OnboardingDeclarationBeneficiaries/OnboardingDeclarationBeneficiaries';
import OnboardingProofOfInvoicing from '@portal/components/OnboardingProofOfInvoicing/OnboardingProofOfInvoicing';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { MaritalStatus } from '@portal/enum/maritalStatusEnum';
import { useNavigation } from '@portal/hooks/useNavigation';

type handleReturn = DocumentToUploadType | undefined;

const OpenAccount = () => {
  const [selectedBullets, setSelectedBullets] = useState<string[]>([]);
  const initialAddressData: models.Address = {
    zipCode: null,
    state: null,
    neighborhood: null,
    city: null,
    street: '',
    addressNumber: null,
    complement: '',
  };
  const initialOccupationsData: models.InitialOccupationsData = {
    company: '',
    months: '',
    years: '',
    cbo: null,
    subOccupation: null,
    grandchild: null,
    greatGrandson: null,
    greatGreatFinish: null,
    validationCbo: true,
  };
  const dispatch = useDispatch();
  const {
    onboarding: {
      createAccount,
      getAddress,
      getAddressError,
      createAddress,
      saveUserPhoto,
      saveUserDocument,
      createPin,
      validateToken,
      taxIdStatus,
      terms,
      cellPhoneStatus,
      cnaeDescription,
      emailStatus,
      settings,
      savePowerOfAttorney,
      saveSocialContract,
      saveMinutesOfElection,
      saveDocument,
      cadastralSituation,
      onboardingCAF,
    },
    loading: { amount: isLoading },
    auth: {
      authToken: { accessToken },
    },
    user: {
      me: { account },
    },
    occupationCbo: {
      cbo,
      cboSubGreatGrandsonOccupation,
      cboSubGreatGreatGrandsonOccupation,
      cboSubOccupation,
    },
  } = useReduxState();

  const navigate = useNavigation<{
    accountType: TaxIdentifierType;
    tab?: TAB_KEY;
    isRetry?: boolean;
    groupType?: AccountGroupType | null;
  }>();

  const initialValuesUserData: models.InitialOnboarding = {
    login: undefined,
    password: undefined,
    name: undefined,
    cellphone: '',
    deviceApprovalCode: '',
    account: undefined,
    representantsIndex: 0,
    representants: [],
    referralCode: '',
    indicationCodeValidation: false,
    cboOccupations: null,
    onboarding: null,
    maritalStatus: 0,
    spouseName: '',
    spouseCpf: '',
    educationLevel: 0,
    nationality: 0,
    bornCity: '',
    bornState: '',
  };

  useEffect(() => {
    dispatch(OnboardingActions.onboardingSettings());
  }, [dispatch]);

  const [tabKey, setTabKey] = useState<TAB_KEY>(TAB_KEY.FIRST_STEP);

  const [idwallSdkToken, setIdwallSdkToken] = useState('');

  const [representantsIndex] = useState(0);
  const [userDataForm, setUserDataForm] = useState<models.InitialOnboarding>(
    initialValuesUserData
  );
  const [acceptTermsOfUse, setAcceptTermsOfUse] = useState(false);
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [acceptAcceptanceOfTarrifs, setAcceptAcceptanceOfTarrifs] =
    useState(false);

  const [addressData, setAddressData] =
    useState<models.Address>(initialAddressData);
  const [occupationsData, setOccupationsData] =
    useState<models.InitialOccupationsData>(initialOccupationsData);
  const [fileSelected, setFileSelected] = useState<File>();
  const [fileSelectedDocumentCustom, setFileSelectedDocumentCustom] =
    useState<File>();
  const [fileSelectedCadastralSituation, setFileSelectedCadastralSituation] =
    useState<File>();
  const [documentType, setDocumentType] = useState(DocumentType.CNH);
  const [fileSelectedDigitalCnh, setFileSelectedDigitalCnh] =
    useState<File | null>();
  const [fileSelectedCnh, setFileSelectedCnh] = useState<File | null>();
  const [fileSelectedCnhBack, setFileSelectedCnhBack] = useState<File | null>();
  const [fileSelectedRgFront, setFileSelectedRgFront] = useState<File | null>();
  const [fileSelectedRgBack, setFileSelectedRgBack] = useState<File | null>();
  const [pin, setPin] = useState<string>('');
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageRecommendation, setShowRecommendation] =
    useState<boolean>(false);
  const [approvalDevice, setApprovalDevice] = useState<boolean>(false);
  const [firstStep, setFirstStep] = useState<boolean>(false);

  const [isValidDate, setIsValidDate] = useState(false);
  const [, setIsValidName] = useState(false);
  const [isUserMinor, setIsUserMinor] = useState(false);
  const [, setIsValidMotherName] = useState(false);
  const [allPercentage, setAllPercentage] = useState(0);

  const [fileSelectedDocument, setFileSelectedDocument] = useState<File>();
  const [fileAnnualIncome, setFileAnnualIncome] = useState<File>();
  const [selectSocialContract, setSelectedSocialContract] = useState<File>();
  const [selectCompanyStatute, setSelectedCompanyStatute] = useState<File>();
  const [selectDeclarationBeneficiaries, setSelectedDeclarationBeneficiaries] =
    useState<File>();
  const [fileSelectedMinutesOfElection, setFileSelectedMinutesOfElection] =
    useState<File>();
  const [fileSelectedProofOfInvoicing, setFileSelectedProofOfInvoicing] =
    useState<File>();
  const [fileSelectedFinancialStatements, setFileSelectedFinancialStatements] =
    useState<File>();
  const [fileSelectedOtherDocuments, setFileSelectedOtherDocuments] =
    useState<File>();
  const [fileSelectedHomeAddressDocument, setFileSelectedHomeAddressDocument] =
    useState<File>();
  const [
    fileSelectedBusinessAddressDocument,
    setFileSelectedBusinessAddressDocument,
  ] = useState<File>();

  const [
    fileSelectedFileOfMembersAndAdministrator,
    setFileSelectedFileOfMembersAndAdministrator,
  ] = useState<File>();

  const noLogin = !!onboarding.cpfLogin || !!onboarding.emailLogin;

  const sendTermsOfUseDataRetry =
    settings?.dataRetry?.termsOfUseDataRetry ?? false;

  const requiresSocialContract =
    onboarding.allowSocialContract &&
    (userDataForm?.account?.companyType === CompanyType.LTDA ||
      account?.companyType === CompanyType.LTDA ||
      userDataForm?.account?.companyType === CompanyType.ME ||
      account?.companyType === CompanyType.ME ||
      userDataForm?.account?.companyType === CompanyType.SA ||
      account?.companyType === CompanyType.SA ||
      onboarding.mandatorySocialContract);

  const requiresCompanyStatute =
    onboarding.allowCompanyStatute &&
    (userDataForm?.account?.companyType === CompanyType.SA ||
      account?.companyType === CompanyType.SA);

  const requiresDeclarationBeneficiaries =
    onboarding.allowDeclarationBeneficiaries &&
    (userDataForm?.account?.companyType === CompanyType.SA ||
      account?.companyType === CompanyType.SA);

  const requiresPowerOfAttorney =
    userDataForm?.representants[0]?.type === RepresentorType.PROCURATOR ||
    (account?.representants &&
      account?.representants[0]?.type === RepresentorType.PROCURATOR);

  const getTermByType = (type: string) => {
    return (
      terms.find((term) => term.type === type) ??
      ({
        token: '',
        type,
        url: '',
      } as models.Terms)
    );
  };

  const handleFileUploadCustom = () => {
    if (fileSelectedDocumentCustom) {
      dispatch(
        OnboardingActions.uploadDocument(
          fileSelectedDocumentCustom.type.split('/')[1],
          fileSelectedDocumentCustom
        )
      );
    }
  };

  const handleFileUploadCadastralSituation = (): handleReturn => {
    if (fileSelectedCadastralSituation) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        fileFirst: fileSelectedCadastralSituation,
        documentType: DocumentType.CNPJ_PROOF,
      };
    }
  };

  useEffect(() => {
    if (fileSelectedDocumentCustom) {
      handleFileUploadCustom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileSelectedDocumentCustom]);

  const handleFileUploadPowerOfAttorney = (): handleReturn => {
    if (fileSelectedDocument) {
      return {
        type: DocumentUploadOrderAndType.POWER_OF_ATTORNEY,
        fileFirst: fileSelectedDocument,
      };
    }
  };

  const handleFileUploadAnnualIncome = (): DocumentToUploadType | undefined => {
    if (onboarding.companyAnnualIncomeDocument && fileAnnualIncome) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.COMPANYANNUALBILLING,
        fileFirst: fileAnnualIncome,
      };
    }
  };

  const handleFileUploadSocialContract = (): handleReturn => {
    if (onboarding.allowSocialContract && selectSocialContract) {
      return {
        type: DocumentUploadOrderAndType.SOCIAL_CONTRACT,
        fileFirst: selectSocialContract,
      };
    }
  };

  const handleFileUploadCompanyStatute = (): handleReturn => {
    if (onboarding.allowCompanyStatute && selectCompanyStatute) {
      return {
        type: DocumentUploadOrderAndType.COMPANY_STATUTE,
        fileFirst: selectCompanyStatute,
      };
    }
  };

  const handleFileUploadDeclarationBeneficiaries = (): handleReturn => {
    if (
      onboarding.allowDeclarationBeneficiaries &&
      selectDeclarationBeneficiaries
    ) {
      return {
        type: DocumentUploadOrderAndType.DECLARATION_OF_BENEFICIARIES,
        fileFirst: selectDeclarationBeneficiaries,
      };
    }
  };

  const handleFileUploadMinutesOfElection = (): handleReturn => {
    if (onboarding.allowMinutesOfElection && fileSelectedMinutesOfElection) {
      return {
        type: DocumentUploadOrderAndType.MINUTES_OF_ELECTION,
        fileFirst: fileSelectedMinutesOfElection,
      };
    }
  };

  const handleFileUploadFileOfMembersAndAdministrator = (): handleReturn => {
    if (
      onboarding.allowFileOfMembersAndAdministrator &&
      fileSelectedFileOfMembersAndAdministrator
    ) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.CORPORATE_STRUCTURE,
        fileFirst: fileSelectedFileOfMembersAndAdministrator,
      };
    }
  };

  const handleFileUploadBusinessAddressDocument = (): handleReturn => {
    if (
      onboarding.allowBusinessAddressDocument &&
      fileSelectedBusinessAddressDocument
    ) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.ADDRESS_PROOF,
        fileFirst: fileSelectedBusinessAddressDocument,
      };
    }
  };

  const handleFileUploadHomeAddressDocument = (): handleReturn => {
    if (
      onboarding.allowHomeAddressDocument &&
      fileSelectedHomeAddressDocument
    ) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.MEMBERS_PROOF_ADDRESS,
        fileFirst: fileSelectedHomeAddressDocument,
      };
    }
  };

  const handleFileUploadProofOfInvoicing = (): handleReturn => {
    if (onboarding.allowProofOfInvoicing && fileSelectedProofOfInvoicing) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.PROOF_OF_INVOICING,
        fileFirst: fileSelectedProofOfInvoicing,
      };
    }
  };

  const handleFileUploadFinancialStatements = (): handleReturn => {
    if (
      onboarding.allowFinancialStatements &&
      fileSelectedFinancialStatements
    ) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.FINANCIAL_STATEMENT,
        fileFirst: fileSelectedFinancialStatements,
      };
    }
  };

  const handleFileUploadOtherDocuments = (): handleReturn => {
    if (onboarding.allowOtherDocuments && fileSelectedOtherDocuments) {
      return {
        type: DocumentUploadOrderAndType.CUSTOM,
        documentType: DocumentType.CUSTOM,
        fileFirst: fileSelectedOtherDocuments,
      };
    }
  };

  const handleFileUpload = (): DocumentToUploadType | undefined => {
    if (fileSelected) {
      return {
        type: DocumentUploadOrderAndType.SELFIE,
        fileFirst: fileSelected,
      } as DocumentToUploadType;
    }
  };

  const handleNewRepresentant = () => {
    const newRepresentant: models.Representants = {
      name: '',
      taxIdentifier: '',
      email: '',
      bornAt: null,
      motherName: '',
      cellphone: '',
      type: 0,
      companyPercentage: 0,
    };
    setUserDataForm((prev) => {
      return {
        ...prev,
        representants: [...prev.representants, newRepresentant],
      };
    });
  };

  const selectOptionsFormatter = (
    array: Array<{ id: number; type: string }> | undefined
  ) => {
    if (array) {
      return array.map((value) => ({
        value: value.id,
        name: value.type,
      }));
    } else {
      return [];
    }
  };

  const onFormChange = (key: string, value: unknown) => {
    setUserDataForm((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const onFormChangeAddress = (key: string, value: string) => {
    setAddressData((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const TabTitle = (title: string) => (
    <>
      <ThemedText variant="h6" color="gray">
        {title}
      </ThemedText>
    </>
  );

  const validatePin = (value: string) => {
    const bornAt =
      navigate?.location?.state?.accountType === TaxIdentifierType.PJ
        ? account?.representants
          ? account.representants[0].bornAt
          : userDataForm.representants[0].bornAt
        : userDataForm.account?.bornAt ?? account.bornAt;

    if (!bornAt) {
      return;
    }
    if (value.length !== 6) {
      throw new Error(PinErrors.INVALID_SIZE);
    }
    if (value.search(/[a-z]+/gi) > 0) {
      throw new Error(PinErrors.MUST_NOT_CONTAIN_LETTERS);
    }
    const birthday = `${bornAt.slice(8)}${bornAt.slice(5, 7)}${bornAt.slice(
      2,
      4
    )}`;
    if (value === unmaskField(birthday)) {
      throw new Error(PinErrors.USER_BIRTHDAY);
    }

    let sequence = 0;
    const alreadySeen: string[] = [];
    const seenTwice: string[] = [];
    value.split('').forEach((digit, index) => {
      if (seenTwice.includes(digit)) {
        throw new Error(PinErrors.SAME_DIGIT_MORE_THAN_TWICE);
      }
      if (alreadySeen.includes(digit)) {
        seenTwice.push(digit);
      }
      alreadySeen.push(digit);
      const diff = parseInt(value.charAt(index + 1), 10) - parseInt(digit, 10);
      if (sequence && sequence === diff) {
        throw new Error(PinErrors.DIGIT_SEQUENCE);
      }
      sequence = Math.abs(diff) === 1 ? diff : 0;
    });
  };

  const getCheckName = (check: models.OnboardingCheck) =>
    typeof check === 'string' ? check : check.name;

  const handleUserData = () => {
    if (
      !onboarding.cpfLogin &&
      !onboarding.emailLogin &&
      (!userDataForm.login ||
        (!/^[a-zA-Z]{1}[_.\-a-zA-Z0-9 ]+$/.test(userDataForm.login) &&
          !onboarding.alphanumericCredentials))
    ) {
      MessageService.error('INVALID_LOGIN', { domain: 'validation' });

      return;
    }

    if (
      userDataForm.password &&
      checkPasswordValidation(validatePassword(userDataForm.password))
    ) {
      const passwordLowerCase = userDataForm?.password.toLowerCase();

      const isBlocked = onboarding.blockedPasswordList.some((blockedWord) =>
        passwordLowerCase.includes(blockedWord)
      );

      if (isBlocked) {
        return MessageService.error('BLOCK_LIST_PASSWORD', {
          domain: 'validation',
        });
      }

      if (userDataForm?.account?.bornAt) {
        const birthday = `${userDataForm?.account?.bornAt.slice(
          8
        )}${userDataForm?.account?.bornAt.slice(
          5,
          7
        )}${userDataForm?.account?.bornAt.slice(0, 4)}`;

        if (passwordLowerCase === unmaskField(birthday)) {
          return MessageService.error('PASSWORD_USER_BIRTHDAY', {
            domain: 'validation',
          });
        }
      }

      if (hasSequence(passwordLowerCase)) {
        return MessageService.error('DIGIT_SEQUENCE', {
          domain: 'validation',
        });
      }

      const requestData: models.OnboardingRequest = {
        login: noLogin ? null : userDataForm.login,
        password: userDataForm.password,
        name: userDataForm.name,
        cellphone: `55${unmaskField(userDataForm.cellphone)}`,
        account: {
          personType: PersonType.PERSON,
          termsOfUse: getTermByType(TermsTypes.TERMS_OF_USE).token ?? '',
          income: userDataForm.account?.income,
          isPep: userDataForm.account?.isPep,
          identityCardIssueDate: userDataForm.account?.identityCardIssueDate,
          identityCardNumber: userDataForm.account?.identityCardNumber,
          privacyPolicy: getTermByType(TermsTypes.PRIVACY_POLICY).token ?? '',
          acceptanceOfTariffs:
            getTermByType(TermsTypes.ACCEPTANCE_OF_TARIFFS).token ?? '',
          type: AccountTaxIdentifierType.PERSON,
          name: userDataForm.account?.name ?? '',
          taxIdentifier: unmaskField(userDataForm.account?.taxIdentifier),
          email: userDataForm.account?.email ?? '',
          cellphone: `55${unmaskField(userDataForm.account?.cellphone)}`,
          bornAt: formatDate(userDataForm.account?.bornAt) ?? '',
          motherName: userDataForm.account?.motherName,
          maritalStatus: userDataForm.maritalStatus ?? null,
          nationality: userDataForm.nationality ?? null,
          bornState: userDataForm.bornState ?? null,
          bornCity: userDataForm.bornCity ?? null,
          educationLevel: userDataForm.educationLevel ?? null,
          usPerson: userDataForm.account?.usPerson,
          taxResidenceAnotherCountry:
            userDataForm.account?.taxResidenceAnotherCountry,
          spouseName: userDataForm.spouseName ?? null,
          spouseCpf: unmaskField(userDataForm.spouseCpf) ?? null,
          groupType: userDataForm.account?.groupType ?? null,
          cboOccupation: userDataForm.cboOccupations ?? null,
          settings: [
            ...onboarding.actions.checks.map((check) => ({
              key: getCheckName(check),
              value: selectedBullets.includes(getCheckName(check)),
              type: 'boolean',
            })),
            ...(onboarding.recommendation
              ? [
                  {
                    key: 'INVITED_BY_CODE',
                    type: 'string',
                    value: userDataForm.referralCode ?? '',
                  },
                ]
              : []),
          ],
        },
        onboarding: {
          onboardingId: onboardingCAF?.onboardingId,
          transactionId: onboardingCAF?.executionId,
        },
      };

      const requestDataCnpj: models.OnboardingRequest = {
        login: noLogin ? null : userDataForm.login,
        password: userDataForm.password,
        name: userDataForm.name,
        cellphone: `55${unmaskField(userDataForm.cellphone)}`,
        account: {
          personType: PersonType.LEGAL,
          termsOfUse: getTermByType(TermsTypes.TERMS_OF_USE).token ?? '',
          privacyPolicy: getTermByType(TermsTypes.PRIVACY_POLICY).token ?? '',
          acceptanceOfTariffs:
            getTermByType(TermsTypes.ACCEPTANCE_OF_TARIFFS).token ?? '',
          type: AccountTaxIdentifierType.CORPORATE,
          name: replaceAmpersandWithE(userDataForm.account?.name) ?? '',
          taxIdentifier: unmaskField(userDataForm.account?.taxIdentifier),
          email: userDataForm.account?.email ?? '',
          cellphone: `55${unmaskField(userDataForm.account?.cellphone)}`,
          bornAt: formatDate(userDataForm.account?.bornAt) ?? '',
          companyType: userDataForm.account?.companyType,
          cnae: userDataForm.account?.cnae,
          income: userDataForm.account?.income,
          usPerson: userDataForm.account?.usPerson,
          nire: userDataForm.account?.nire,
          taxResidenceAnotherCountry:
            userDataForm.account?.taxResidenceAnotherCountry,
          isPep: userDataForm.account?.isPep,
          businessLine: userDataForm.account?.businessLine ?? 47,
          establishmentForm: 1,
          fantasyName: replaceAmpersandWithE(userDataForm.account?.fantasyName),
          groupType: userDataForm.account?.groupType ?? null,
          representor: {
            name: userDataForm.representants[0]?.name ?? '',
            taxIdentifier: unmaskField(
              userDataForm.representants[0]?.taxIdentifier
            ),
            email: userDataForm.representants[0]?.email,
            motherName: userDataForm.representants[0]?.motherName,
            bornAt: formatDate(userDataForm.representants[0]?.bornAt) ?? '',
            cellphone: `55${unmaskField(
              userDataForm.representants[0]?.cellphone
            )}`,
            type: userDataForm.representants[0]?.type ?? 1,
            companyPercentage: userDataForm.representants[0]?.companyPercentage,
            address: userDataForm.representants[0]?.address,
            identityCardIssueDate:
              userDataForm.representants[0]?.identityCardIssueDate,
            identityCardNumber:
              userDataForm.representants[0]?.identityCardNumber,
            main: userDataForm.representants[0]?.main,
          },
          settings: [
            ...onboarding.actions.checks.map((check) => ({
              key: getCheckName(check),
              value: selectedBullets.includes(getCheckName(check)),
              type: 'boolean',
            })),
            ...(onboarding.recommendation
              ? [
                  {
                    key: 'INVITED_BY_CODE',
                    type: 'string',
                    value: userDataForm.referralCode ?? '',
                  },
                ]
              : []),
          ],
        },
      };

      dispatch(
        OnboardingActions.createAccount(
          navigate?.location?.state?.accountType === TaxIdentifierType.PF
            ? requestData
            : requestDataCnpj
        )
      );
      if (navigate?.location?.state?.accountType === TaxIdentifierType.PJ) {
        setAllPercentage(userDataForm.representants[0]?.companyPercentage ?? 0);
      }
    } else {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.PASSWORD',
        { domain: 'validation' }
      );
    }
  };

  useEffect(() => {
    if (
      navigate?.location?.state?.accountType === TaxIdentifierType.PJ &&
      account &&
      account.representants &&
      !navigate?.location?.state?.isRetry
    ) {
      const userPercentage = account.representants.reduce(
        (totalPercentage, { companyPercentage }) =>
          totalPercentage + (Number(companyPercentage) || 0),
        0
      );
      setAllPercentage(userPercentage);
    }

    if (
      navigate?.location?.state?.accountType === TaxIdentifierType.PF &&
      account &&
      account.bornAt &&
      onboarding.allowMinor
    ) {
      const isMinor = DateTime.now()
        .diff(DateTime.fromISO(account.bornAt), 'year')
        .toObject();

      if (
        isMinor.years &&
        navigate?.location?.state?.accountType === TaxIdentifierType.PF
      ) {
        setIsUserMinor(isMinor.years < 18);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    if (validateToken && approvalDevice) {
      if (navigate?.location?.state?.accountType === TaxIdentifierType.PF) {
        setAcceptTermsOfUse(false);
        setAcceptPrivacyPolicy(false);

        if (onboarding.occupations) {
          return setTabKey(TAB_KEY.OCCUPATIONS);
        } else if (onboarding.recommendation) {
          return setTabKey(TAB_KEY.RECOMMENDATION);
        } else {
          setTabKey(TAB_KEY.TERMS_OF_USE);
        }
      } else if (
        userDataForm.representants[0]?.type === RepresentorType.PROCURATOR
      ) {
        setTabKey(TAB_KEY.PIN);
      } else {
        if (userDataForm.representants.length <= 1) {
          handleNewRepresentant();
        }
        setTabKey(TAB_KEY.REPRESENTANTS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateToken]);

  useEffect(() => {
    if (
      emailStatus === validateStatus.OK &&
      cellPhoneStatus === validateStatus.OK &&
      taxIdStatus === validateStatus.OK &&
      firstStep
    ) {
      dispatch({ type: ONBOARDING_EMAIL_STATUS, payload: null });
      dispatch({ type: ONBOARDING_CELLPHONE_STATUS, payload: null });
      dispatch({ type: ONBOARDING_TAX_ID_STATUS, payload: null });

      setTabKey(TAB_KEY.APPROVAL_DEVICE);
    }
  }, [cellPhoneStatus, taxIdStatus, emailStatus, firstStep, dispatch]);

  useEffect(() => {
    if (cellPhoneStatus === validateStatus.IN_USE) {
      MessageService.error('PAGES.AUTH.REGISTER_NUMBER.ERRORS.IN_USE', {
        domain: 'validation',
      });
    }
  }, [cellPhoneStatus]);

  useEffect(() => {
    if (taxIdStatus === validateStatus.IN_USE) {
      MessageService.error('PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CPF_IN_USE', {
        domain: 'validation',
      });
    }
  }, [taxIdStatus]);

  useEffect(() => {
    if (emailStatus === validateStatus.IN_USE) {
      MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.EMAIL_IN_USE',
        { domain: 'validation' }
      );
    }
  }, [emailStatus]);

  const handleApprovalDevice = () => {
    if (userDataForm.deviceApprovalCode?.length === 6) {
      dispatch(
        OnboardingActions.onboardingValidateDevice({
          cellphone: `55${unmaskField(userDataForm.cellphone)}`,
          code: userDataForm.deviceApprovalCode
            ? userDataForm.deviceApprovalCode
            : '',
        })
      );
      setApprovalDevice(true);
    }
  };

  const handleRecommendation = () => {
    if (
      userDataForm.referralCode === '' ||
      userDataForm.indicationCodeValidation
    ) {
      if (navigate?.location?.state?.accountType === TaxIdentifierType.PF) {
        setTabKey(TAB_KEY.TERMS_OF_USE);
      } else {
        setTabKey(TAB_KEY.REPRESENTANTS);
      }
    } else {
      return setShowRecommendation(true);
    }
  };

  const handleOccupations = () => {
    const occupationStructure = createOccupationStructure(
      occupationsData.cbo,
      occupationsData.subOccupation,
      occupationsData.grandchild,
      occupationsData.greatGrandson,
      occupationsData.greatGreatFinish
    );

    if (!occupationsData.company) {
      return MessageService.error('INVALID_COMPANY_NAME', {
        domain: 'validation',
      });
    }

    if (
      !occupationsData.years &&
      !occupationsData.months &&
      onboarding.onboardingOccupancyTime
    ) {
      return MessageService.error('DATE_INVALID', { domain: 'validation' });
    }

    if (
      (cbo !== null && occupationsData.cbo === null) ||
      (occupationsData.validationCbo &&
        occupationsData.subOccupation === null) ||
      (cboSubOccupation !== null && occupationsData.grandchild === null) ||
      (cboSubGreatGrandsonOccupation !== null &&
        occupationsData.greatGrandson === null) ||
      (cboSubGreatGreatGrandsonOccupation !== null &&
        occupationsData.greatGreatFinish === null)
    ) {
      return MessageService.error('OCCUPATION_ERROR', { domain: 'validation' });
    }

    if (!userDataForm.educationLevel && onboarding.allowEducation) {
      return MessageService.error('INVALID_EDUCATION', {
        domain: 'validation',
      });
    }

    if (!userDataForm.nationality && onboarding.allowNationality) {
      return MessageService.error('INVALID_NATIONALITY', {
        domain: 'validation',
      });
    }

    if (
      userDataForm.nationality === CountriesEnum.BRL &&
      !userDataForm.bornState &&
      onboarding.allowNationality
    ) {
      return MessageService.error('INVALID_STATE', { domain: 'validation' });
    }

    if (
      userDataForm.nationality === CountriesEnum.BRL &&
      !userDataForm.bornCity &&
      onboarding.allowNationality
    ) {
      return MessageService.error('INVALID_CITY', { domain: 'validation' });
    }

    if (!userDataForm.maritalStatus && onboarding.allowMaritalStatus) {
      return MessageService.error('INVALID_MARITAL_STATUS', {
        domain: 'validation',
      });
    }

    if (
      !userDataForm.spouseName &&
      onboarding.allowSpouseName &&
      userDataForm.maritalStatus === MaritalStatus.MARRIED
    ) {
      return MessageService.error('INVALID_SPOUSE_NAME', {
        domain: 'validation',
      });
    }

    if (
      !userDataForm.spouseCpf &&
      onboarding.allowSpouseCPF &&
      userDataForm.maritalStatus === MaritalStatus.MARRIED
    ) {
      return MessageService.error('INVALID_SPOUSE_CPF', {
        domain: 'validation',
      });
    }

    const cboOccupations = {
      company: occupationsData.company,
      months: occupationsData.months,
      occupations: occupationStructure,
      years: occupationsData.years,
    };

    setUserDataForm((prev) => {
      return {
        ...prev,
        cboOccupations: cboOccupations,
      };
    });

    if (onboarding.recommendation) {
      setTabKey(TAB_KEY.RECOMMENDATION);
    } else {
      setTabKey(TAB_KEY.TERMS_OF_USE);
    }
  };

  const formatDate = (value: null | string | undefined) => {
    if (value) {
      const isoDate = value.split('T');

      const formatDate = isoDate && isoDate[0].split('-');

      const year = formatDate && formatDate[2];
      const month = formatDate && formatDate[1];
      const day = formatDate && formatDate[0];
      return `${year}/${month}/${day}`;
    }
  };

  const handleAddress = () => {
    if (!navigate?.location?.state?.isRetry) {
      const requestData: models.Address = {
        zipCode: unmaskField(addressData.zipCode ?? ''),
        state: addressData.state,
        neighborhood: removeSpecialChars(addressData.neighborhood ?? ''),
        city: removeSpecialChars(addressData.city ?? ''),
        street: removeSpecialChars(addressData.street ?? ''),
        addressNumber: addressData.addressNumber,
      };

      for (const [, value] of Object.entries(requestData)) {
        if (!value) {
          MessageService.error('ALL_FIELDS', { domain: 'validation' });
          return;
        }
      }

      dispatch(
        OnboardingActions.createAddress({
          ...requestData,
          complement: removeSpecialChars(addressData.complement ?? ''),
        })
      );

      return;
    }

    switch (navigate?.location?.state?.accountType) {
      case TaxIdentifierType.PF: {
        if (isUserMinor) {
          setTabKey(TAB_KEY.MINOR_REPRESENTANTS);

          return;
        }

        const requestDataPF: models.DataRetryRequest = {
          account: {
            name: userDataForm.account?.name ?? '',
            taxIdentifier: unmaskField(userDataForm.account?.taxIdentifier),
            email: userDataForm.account?.email ?? '',
            bornAt: formatDate(userDataForm.account?.bornAt) ?? '',
            motherName: userDataForm.account?.motherName,
            monthlyIncome: userDataForm.account?.income,
            ...(userDataForm.account?.financialStatistic && {
              financialStatistic: userDataForm.account.financialStatistic,
            }),
            address: {
              zipCode: unmaskField(addressData.zipCode ?? ''),
              state: addressData.state,
              neighborhood: removeSpecialChars(addressData.neighborhood ?? ''),
              city: removeSpecialChars(addressData.city ?? ''),
              street: removeSpecialChars(addressData.street ?? ''),
              addressNumber: addressData.addressNumber,
              complement: removeSpecialChars(addressData.complement ?? ''),
            },
            ...(sendTermsOfUseDataRetry && {
              termsOfUse: getTermByType(TermsTypes.TERMS_OF_USE).token ?? '',
              privacyPolicy:
                getTermByType(TermsTypes.PRIVACY_POLICY).token ?? '',
              acceptanceOfTariffs:
                getTermByType(TermsTypes.ACCEPTANCE_OF_TARIFFS).token ?? '',
            }),
          },
        };
        dispatch(OnboardingActions.dataRetry(requestDataPF));

        return;
      }

      case TaxIdentifierType.PJ: {
        const requestDataPJ: models.DataRetryRequest = {
          account: {
            name: replaceAmpersandWithE(userDataForm.account?.name) ?? '',
            taxIdentifier: unmaskField(userDataForm.account?.taxIdentifier),
            email: userDataForm.account?.email ?? '',
            bornAt:
              formatDate(userDataForm.account?.bornAt) ??
              formatDate(
                (
                  userDataForm.account as models.CreateAccount & {
                    openDate?: string;
                  }
                )?.openDate
              ) ??
              '',
            companyType: userDataForm.account?.companyType,
            cnae: userDataForm.account?.cnae,
            businessLine: userDataForm.account?.businessLine ?? 47,
            establishmentForm: userDataForm.account?.establishmentForm ?? 1,
            fantasyName: replaceAmpersandWithE(
              userDataForm.account?.fantasyName
            ),
            ...(userDataForm.account?.businessRegistration && {
              businessRegistration: userDataForm.account.businessRegistration
                ? Number(userDataForm.account.businessRegistration) || null
                : null,
            }),
            monthlyIncome: userDataForm.account?.income,
            groupType: userDataForm.account?.groupType ?? null,
            ...(userDataForm.account?.financialStatistic && {
              financialStatistic: userDataForm.account.financialStatistic,
            }),
            address: {
              zipCode: unmaskField(addressData.zipCode ?? ''),
              state: addressData.state,
              neighborhood: removeSpecialChars(addressData.neighborhood ?? ''),
              city: removeSpecialChars(addressData.city ?? ''),
              street: removeSpecialChars(addressData.street ?? ''),
              addressNumber: addressData.addressNumber,
              complement: removeSpecialChars(addressData.complement ?? ''),
            },
            ...(sendTermsOfUseDataRetry && {
              termsOfUse: getTermByType(TermsTypes.TERMS_OF_USE).token ?? '',
              privacyPolicy:
                getTermByType(TermsTypes.PRIVACY_POLICY).token ?? '',
              acceptanceOfTariffs:
                getTermByType(TermsTypes.ACCEPTANCE_OF_TARIFFS).token ?? '',
            }),
          },
        };
        dispatch(OnboardingActions.dataRetry(requestDataPJ));

        return;
      }

      default:
        return;
    }
  };

  const handleUploadDocument = (): DocumentToUploadType | undefined => {
    if (documentType === DocumentType.CNH && fileSelectedCnh) {
      return {
        type: DocumentUploadOrderAndType.DOCUMENT_CHN,
        fileFirst: fileSelectedCnh,
        fileSecond: fileSelectedCnhBack,
      } as DocumentToUploadType;
    }

    if (documentType === DocumentType.DIGITAL_CNH && fileSelectedDigitalCnh) {
      return {
        type: DocumentUploadOrderAndType.DIGITAL_CNH,
        fileFirst: fileSelectedDigitalCnh,
      } as DocumentToUploadType;
    }

    if (
      (documentType === DocumentType.RG ||
        documentType === DocumentType.RG_BACK ||
        documentType === DocumentType.RG_FRONT) &&
      fileSelectedRgFront &&
      fileSelectedRgBack
    ) {
      return {
        type: DocumentUploadOrderAndType.DOCUMENT_RG,
        fileFirst: fileSelectedRgFront,
        fileSecond: fileSelectedRgBack,
      } as DocumentToUploadType;
    }
  };

  const handleFirstStepPerson = (newAccount: models.CreateAccount) => {
    setUserDataForm((prev) => {
      return {
        ...prev,
        name: `${newAccount.name}`,
        cellphone: `${newAccount.cellphone}`,
        account: newAccount,
      };
    });

    if (!navigate?.location?.state?.isRetry) {
      const hasEmptyRequiredFields =
        !newAccount.taxIdentifier || !newAccount.email || !newAccount.cellphone;

      if (hasEmptyRequiredFields) {
        return MessageService.error(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS',
          { domain: 'validation' }
        );
      }
    }

    if (
      !validateEmail(newAccount.email ?? '') &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.EMAIL_INVALID',
        { domain: 'validation' }
      );
    }

    if (onboarding.allowGender && !newAccount.gender) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.GENDER',
        { domain: 'validation' }
      );
    }

    if (
      (newAccount && !newAccount.bornAt && !onboarding.isOnboardingCaf) ||
      (!isValidDate && !onboarding.isOnboardingCaf)
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.BIRTH_DATE',
        { domain: 'validation' }
      );
    }

    if (
      !navigate?.location?.state?.isRetry &&
      !validatePhone(newAccount.cellphone ?? '')
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CELLPHONE',
        { domain: 'validation' }
      );
    }

    if (onboarding.disableUnderage && isUserMinor) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.USER_IS_MINOR',
        { domain: 'validation' }
      );
    }

    if (
      !newAccount.financialStatistic &&
      onboarding.moreOptions &&
      !isUserMinor &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.FINANCIAL_STATISTICS',
        { domain: 'validation' }
      );
    }

    if (
      !newAccount.gender &&
      onboarding.moreOptions &&
      !isUserMinor &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.GENDER',
        { domain: 'validation' }
      );
    }

    if (
      !newAccount.businessLine &&
      onboarding.moreOptions &&
      !isUserMinor &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.BUSINESS_LINE',
        { domain: 'validation' }
      );
    }

    if (
      newAccount.gender !== UserGender.FEMALE &&
      onboarding.moreOptions &&
      !isUserMinor
    ) {
      newAccount.gender = UserGender.MALE;
    }

    if (
      (!newAccount ||
        !newAccount.taxIdentifier ||
        !validateCPF(newAccount.taxIdentifier)) &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error('PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CPF', {
        domain: 'validation',
      });
    }

    if (
      newAccount.motherName &&
      newAccount.name &&
      (newAccount.motherName.length === 0 ||
        !newAccount.motherName.split(' ')[1] ||
        newAccount.name.length === 0 ||
        !newAccount.name.split(' ')[1])
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.NAME',
        { domain: 'validation' }
      );
    }

    if (
      (!onboarding.isOnboardingCaf && !newAccount.name) ||
      (!onboarding.isOnboardingCaf && !newAccount.motherName)
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS',
        { domain: 'validation' }
      );
    }

    if (
      !onboarding.isOnboardingCaf &&
      onboarding.incomePf &&
      onboarding.requireIncome
        ? !newAccount.income
        : (newAccount.income ?? 0) < 0
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.INCOME',
        { domain: 'validation' }
      );
    }

    if (!navigate?.location?.state?.isRetry) {
      dispatch(
        OnboardingActions.validateTaxId(unmaskField(newAccount.taxIdentifier))
      );
      dispatch(OnboardingActions.validateEmail(newAccount.email ?? ''));
      dispatch(
        OnboardingActions.validateCellphone(unmaskField(newAccount.cellphone))
      );
      setFirstStep(true);
    } else {
      setTabKey(TAB_KEY.ADDRESS);
    }
  };

  const handleFirstStepCompany = (newAccount: models.CreateAccount) => {
    setUserDataForm((prev) => {
      return {
        ...prev,
        name: `${newAccount.name}`,
        cellphone: `${newAccount.cellphone}`,
        account: {
          ...newAccount,
          groupType: navigate?.location?.state?.groupType,
        },
      };
    });

    if (
      !validateEmail(newAccount.email ?? '') &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.EMAIL_INVALID',
        { domain: 'validation' }
      );
    }

    if (
      onboarding.allowNire &&
      (!newAccount.nire || newAccount.nire?.length < 11)
    ) {
      return MessageService.error('NIRE_INVALID', { domain: 'validation' });
    }

    if (
      (newAccount && !newAccount.bornAt) ||
      (!isValidDate && !onboarding.isOnboardingCaf)
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.BIRTH_DATE',
        { domain: 'validation' }
      );
    }

    if (
      (!newAccount || !newAccount.cellphone) &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CELLPHONE',
        { domain: 'validation' }
      );
    }

    if (!newAccount?.financialStatistic && onboarding.moreOptions) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.FINANCIAL_STATISTICS',
        { domain: 'validation' }
      );
    }

    if (!newAccount?.businessLine && onboarding.allowCompanyBranch) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.COMPANY_BRANCH',
        { domain: 'validation' }
      );
    }

    if (
      !newAccount?.companyType ||
      !newAccount?.cnae ||
      (onboarding.requireFantasyName && !newAccount?.fantasyName) ||
      onboarding.requireIncome
        ? !newAccount?.income
        : (newAccount?.income ?? 0) < 0 || !newAccount?.name
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS',
        { domain: 'validation' }
      );
    }
    if (
      (!newAccount ||
        !newAccount.taxIdentifier ||
        !validateCNPJ(newAccount.taxIdentifier)) &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CNPJ',
        { domain: 'validation' }
      );
    }

    if (!cnaeDescription && onboarding.cnaeValidation) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.NOT_VALID_CNAE',
        { domain: 'validation' }
      );
    }

    if (
      !!ONBOARDING_MAXLENGTH_CNAE &&
      !(
        (newAccount && newAccount.cnae?.length) === ONBOARDING_MAXLENGTH_CNAE
      ) &&
      onboarding.inputMaxLength
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.NOT_VALID_CNAE_LENGTH',
        { domain: 'validation' }
      );
    }

    if (!newAccount.companyType) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PJ.ERRORS.COMPANY_TYPE_EMPTY',
        { domain: 'validation' }
      );
    }

    if (!navigate?.location?.state?.isRetry) {
      dispatch(
        OnboardingActions.validateTaxId(unmaskField(newAccount?.taxIdentifier))
      );
      dispatch(OnboardingActions.validateEmail(newAccount?.email ?? ''));
      dispatch(
        OnboardingActions.validateCellphone(unmaskField(newAccount?.cellphone))
      );
      setFirstStep(true);
    } else {
      if (userDataForm.representants.length <= 1) {
        handleNewRepresentant();
      }

      if (account.representants?.length === 1) {
        setTabKey(TAB_KEY.NEW_REPRESENTANTS);
      } else {
        setTabKey(TAB_KEY.REPRESENTANTS);
      }
    }
  };

  const handleRepresentsValidation = (
    newRepresentative: models.Representants
  ) => {
    setUserDataForm((prev) => {
      const updatedRepresentants = [...prev.representants];
      updatedRepresentants[representantsIndex] = newRepresentative;
      return {
        ...prev,
        representants: updatedRepresentants,
      };
    });

    if (
      !validateEmail(newRepresentative.email ?? '') &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.EMAIL_INVALID',
        { domain: 'validation' }
      );
    }

    if (
      (!newRepresentative.taxIdentifier ||
        (!validateCPF(newRepresentative.taxIdentifier) &&
          !validateCNPJ(newRepresentative.taxIdentifier))) &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error('PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CPF', {
        domain: 'validation',
      });
    }

    if (
      !validatePhone(newRepresentative.cellphone ?? '') &&
      !navigate?.location?.state?.isRetry
    ) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CELLPHONE',
        { domain: 'validation' }
      );
    }

    if (
      !newRepresentative.name ||
      !newRepresentative.taxIdentifier ||
      !newRepresentative.email
    ) {
      if (!navigate?.location?.state?.isRetry) {
        return MessageService.error(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS',
          { domain: 'validation' }
        );
      }
    }

    if (navigate?.location?.state?.isRetry) {
      if (navigate?.location?.state?.accountType === TaxIdentifierType.PJ) {
        setTabKey(TAB_KEY.ADDRESS);
        return;
      }

      setTabKey(TAB_KEY.TERMS_OF_USE);
      return;
    }

    setTabKey(TAB_KEY.TERMS_OF_USE);
  };

  const handlePin = () => {
    try {
      validatePin(pin);
      dispatch(
        OnboardingActions.createPin({
          token: pin,
          type: UserCredentialType.PIN,
        })
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    }
  };

  const validateChecks = (bullets: string[]) =>
    onboarding.actions.checks.some(
      (check) =>
        typeof check !== 'string' &&
        check['mandatory'] &&
        !bullets.includes(check['name'] ?? '')
    );

  const handleLogout = () => {
    StorageService.removeItem('session-token');
    dispatch(AuthActions.logout());
    navigate('/');
  };

  const LogoutButton = () => (
    <div className="advance_final__button--logout">
      <AdvancedButton
        text={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.ANOTHER_ACCOUNT'
        )}
        onClick={handleLogout}
        testId="logout-button"
      />
    </div>
  );

  const AdvanceApprovalDeviceStep = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => {
          setTabKey(TAB_KEY.FIRST_STEP);
        }}
        testId="advance-approval-device-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          onClick={handleApprovalDevice}
          disabled={
            !userDataForm.deviceApprovalCode ||
            userDataForm.deviceApprovalCode.length < 6
          }
          testId="advance-approval-device-submit"
        />
      </div>
    </Row>
  );

  const RecommendationDeviceStep = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => {
          if (
            onboarding.occupations &&
            navigate?.location?.state?.accountType === TaxIdentifierType.PF
          ) {
            setTabKey(TAB_KEY.OCCUPATIONS);
          } else {
            setTabKey(TAB_KEY.APPROVAL_DEVICE);
          }
        }}
        testId="recommendation-device-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          onClick={handleRecommendation}
          testId="recommendation-device-submit"
        />
      </div>
    </Row>
  );

  const OccupationDeviceStep = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => setTabKey(TAB_KEY.APPROVAL_DEVICE)}
        testId="occupation-device-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          onClick={handleOccupations}
          testId="occupation-device-submit"
        />
      </div>
    </Row>
  );

  const validateCredentials = () =>
    (!onboarding.cpfLogin &&
      !onboarding.emailLogin &&
      (!userDataForm.login ||
        (!/^[a-zA-Z]{1}[_.\-a-zA-Z0-9 ]+$/.test(userDataForm.login) &&
          !onboarding.alphanumericCredentials))) ||
    !(
      userDataForm.password &&
      checkPasswordValidation(validatePassword(userDataForm.password))
    );

  const AdvanceCredentials = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => {
          setAcceptTermsOfUse(false);
          setAcceptPrivacyPolicy(false);
          setTabKey(
            onboarding.privacyPolicy
              ? TAB_KEY.PRIVACY_POLICY
              : TAB_KEY.TERMS_OF_USE
          );
        }}
        testId="advance-credentials-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          onClick={() => handleUserData()}
          disabled={validateCredentials() || validateChecks(selectedBullets)}
          testId="advance-credentials-submit"
        />
      </div>
    </Row>
  );

  const AdvanceTermsOfUse = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => {
          if (navigate?.location?.state?.accountType === TaxIdentifierType.PF) {
            if (onboarding.recommendation) {
              return setTabKey(TAB_KEY.RECOMMENDATION);
            } else if (onboarding.occupations) {
              return setTabKey(TAB_KEY.OCCUPATIONS);
            }
            setTabKey(TAB_KEY.APPROVAL_DEVICE);
          } else {
            setTabKey(TAB_KEY.REPRESENTANTS);
          }
        }}
        testId="advance-terms-of-use-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          disabled={!acceptTermsOfUse}
          onClick={() => {
            if (onboarding.privacyPolicy) {
              {
                onboarding.isOnboardingCaf
                  ? setTabKey(TAB_KEY.DOCUMENT_CAF)
                  : setTabKey(TAB_KEY.PRIVACY_POLICY);
              }
            } else if (onboarding.acceptanceOfTariffs) {
              setTabKey(TAB_KEY.ACCEPTANCE_OF_TARIFFS);
            } else if (
              navigate?.location?.state?.isRetry &&
              sendTermsOfUseDataRetry
            ) {
              setTabKey(TAB_KEY.ADDRESS);
            } else {
              setTabKey(TAB_KEY.CREDENTIALS);
            }
          }}
          testId="advance-terms-of-use-submit"
        />
      </div>
    </Row>
  );

  const AdvancePrivacyPolicy = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => {
          setTabKey(TAB_KEY.TERMS_OF_USE);
        }}
        testId="advance-privacy-policy-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          disabled={!acceptPrivacyPolicy}
          onClick={() => {
            if (onboarding.acceptanceOfTariffs) {
              setTabKey(TAB_KEY.ACCEPTANCE_OF_TARIFFS);
            } else if (
              navigate?.location?.state?.isRetry &&
              sendTermsOfUseDataRetry
            ) {
              setTabKey(TAB_KEY.ADDRESS);
            } else {
              setTabKey(TAB_KEY.CREDENTIALS);
            }
          }}
          testId="advance-privacy-policy-submit"
        />
      </div>
    </Row>
  );

  const advanceAcceptanceOfTariffs = (
    <Row className="advance_final">
      <AdvancedButton
        text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL')}
        variant="outlined"
        onClick={() => {
          setTabKey(TAB_KEY.TERMS_OF_USE);
        }}
        testId="advance-acceptance-of-tariffs-back"
      />
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          disabled={!acceptAcceptanceOfTarrifs}
          onClick={() => {
            if (navigate?.location?.state?.isRetry && sendTermsOfUseDataRetry) {
              setTabKey(TAB_KEY.ADDRESS);
            } else {
              setTabKey(TAB_KEY.CREDENTIALS);
            }
          }}
          testId="advance-acceptance-of-tariffs-submit"
        />
      </div>
    </Row>
  );

  const AdvanceAddress = (
    <Row className="advance_final">
      {onboarding.enableLogoutOnboardingButton ? <LogoutButton /> : <div />}
      <div className="advance_final__button">
        <AdvancedButton
          disabled={
            !addressData.zipCode ||
            !addressData.neighborhood ||
            !addressData.street ||
            !addressData.addressNumber
          }
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          onClick={() => handleAddress()}
          testId="advance-address-submit"
        />
      </div>
    </Row>
  );

  const validateFiles = () => {
    const validationArray: (File | undefined)[] = [];

    if (navigate?.location?.state?.accountType === TaxIdentifierType.PJ) {
      if (onboarding.allowCnpjCard)
        validationArray.push(fileSelectedCadastralSituation);
      if (requiresSocialContract) validationArray.push(selectSocialContract);
      if (requiresCompanyStatute) validationArray.push(selectCompanyStatute);
      if (requiresDeclarationBeneficiaries)
        validationArray.push(selectDeclarationBeneficiaries);
      if (onboarding.companyAnnualIncomeDocument)
        validationArray.push(fileAnnualIncome);
    }

    const isValid =
      validatePersonalDocument() &&
      validationArray.every((file) => file !== undefined);

    return isValid;
  };

  const validatePersonalDocument = () => {
    if (onboarding.useIdwall) {
      return true;
    }

    if (documentType === DocumentType.CNH) {
      return onboarding.requireTwodriverLicensePictures
        ? fileSelectedCnh !== undefined && fileSelectedCnhBack !== undefined
        : fileSelectedCnh !== undefined;
    } else if (documentType === DocumentType.RG) {
      return (
        fileSelectedRgFront !== undefined && fileSelectedRgBack !== undefined
      );
    } else if (documentType === DocumentType.DIGITAL_CNH) {
      return fileSelectedDigitalCnh !== undefined;
    }

    return false;
  };

  const handleOnUploadDocuments = async () => {
    if (onboarding.useIdwall && tabKey === TAB_KEY.IDWALL_VERIFICATION) {
      await dispatch(
        OnboardingActions.uploadPhoto(
          '',
          undefined,
          false,
          idwallSdkToken,
          undefined,
          navigate?.location?.state?.accountType === TaxIdentifierType.PJ
        )
      );

      if (navigate?.location?.state?.accountType === TaxIdentifierType.PF) {
        setTabKey(TAB_KEY.PIN);
      }

      return;
    }

    const filesArray: DocumentToUploadType[] = [];

    const fileUpload = handleFileUpload();

    if (fileUpload) filesArray.push(fileUpload);

    const uploadDocument = handleUploadDocument();

    if (uploadDocument) filesArray.push(uploadDocument);

    if (navigate?.location?.state?.accountType === TaxIdentifierType.PJ) {
      if (onboarding.mandatorySocialContract && !selectSocialContract) {
        return MessageService.error('MANDATORY_SOCIAL_CONTRACT');
      }

      if (onboarding.companyAnnualIncomeDocument && !fileAnnualIncome) {
        return MessageService.error('MANDATORY_ANNUAL_INCOME');
      }

      const cadastralSituation = handleFileUploadCadastralSituation();
      if (cadastralSituation) filesArray.push(cadastralSituation);

      if (requiresSocialContract) {
        const fileUploadSocialContract = handleFileUploadSocialContract();
        if (fileUploadSocialContract) filesArray.push(fileUploadSocialContract);
      }

      if (requiresCompanyStatute) {
        const fileUploadCompanyStatute = handleFileUploadCompanyStatute();
        if (fileUploadCompanyStatute) filesArray.push(fileUploadCompanyStatute);
      }

      if (requiresDeclarationBeneficiaries) {
        const fileUploadDeclarationBeneficiaries =
          handleFileUploadDeclarationBeneficiaries();
        if (fileUploadDeclarationBeneficiaries)
          filesArray.push(fileUploadDeclarationBeneficiaries);
      }

      const minutesOfElection = handleFileUploadMinutesOfElection();
      if (minutesOfElection) filesArray.push(minutesOfElection);

      if (onboarding.allowFileOfMembersAndAdministrator) {
        const fileOfMembersAndAdministrator =
          handleFileUploadFileOfMembersAndAdministrator();
        if (fileOfMembersAndAdministrator)
          filesArray.push(fileOfMembersAndAdministrator);
      }

      if (onboarding.allowBusinessAddressDocument) {
        const businessAddressProof = handleFileUploadBusinessAddressDocument();
        if (businessAddressProof) filesArray.push(businessAddressProof);
      }

      if (onboarding.allowHomeAddressDocument) {
        const homeAddressProof = handleFileUploadHomeAddressDocument();
        if (homeAddressProof) filesArray.push(homeAddressProof);
      }

      if (onboarding.allowProofOfInvoicing) {
        const proofOfInvoicing = handleFileUploadProofOfInvoicing();
        if (proofOfInvoicing) filesArray.push(proofOfInvoicing);
      }

      if (onboarding.allowFinancialStatements) {
        const financialStatements = handleFileUploadFinancialStatements();
        if (financialStatements) filesArray.push(financialStatements);
      }

      if (requiresPowerOfAttorney) {
        const fileUploadDocument = handleFileUploadPowerOfAttorney();
        if (fileUploadDocument) filesArray.push(fileUploadDocument);
      }

      if (onboarding.companyAnnualIncomeDocument) {
        const fileAnnualDocuument = handleFileUploadAnnualIncome();
        if (fileAnnualDocuument) filesArray.push(fileAnnualDocuument);
      }

      if (onboarding.allowOtherDocuments) {
        const otherDocuments = handleFileUploadOtherDocuments();
        if (otherDocuments) filesArray.push(otherDocuments);
      }
    }

    await dispatch(OnboardingActions.uploadDocumentsArray(filesArray));
    setTabKey(TAB_KEY.PIN);
  };

  const AdvanceDocument = (
    <Row className="advance_final">
      {onboarding.enableLogoutOnboardingButton ? <LogoutButton /> : <div />}
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          disabled={!validateFiles()}
          onClick={handleOnUploadDocuments}
          testId="advance-document-submit"
        />
      </div>
    </Row>
  );

  const AdvancePin = (
    <Row className="advance_final">
      {onboarding.enableLogoutOnboardingButton ? <LogoutButton /> : <div />}
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          onClick={() => handlePin()}
          disabled={pin.length < 6}
          fullWidth
          testId="advance-pin-submit"
        />
      </div>
    </Row>
  );

  const AdvanceFinal = (
    <Row className="advance_final">
      {onboarding.enableLogoutOnboardingButton ? <LogoutButton /> : <div />}
      <div className="advance_final__button">
        <AdvancedButton
          text={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.CLOSE')}
          onClick={() => {
            sendEvent(EventType.SIGN_UP);
            handleLogout();
          }}
          testId="advance-final-close"
        />
      </div>
    </Row>
  );

  const AdvanceIdwall = (
    <Row className="advance_final">
      {onboarding.enableLogoutOnboardingButton ? <LogoutButton /> : <div />}
      <div className="advance_final__button">
        <AdvancedButton
          text={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
          )}
          disabled={!idwallSdkToken}
          onClick={handleOnUploadDocuments}
          testId="advance-idwall-submit"
        />
      </div>
    </Row>
  );

  useEffect(() => {
    if (createAccount) {
      setTabKey(TAB_KEY.ADDRESS);
    }
  }, [createAccount, dispatch]);

  useEffect(() => {
    const cleanZipCode = unmaskField(addressData?.zipCode);
    if (cleanZipCode && cleanZipCode.length === 8) {
      dispatch(OnboardingActions.getAddress(cleanZipCode));
    }
  }, [addressData?.zipCode, dispatch]);

  useEffect(() => {
    if (getAddress) {
      setAddressData((prev) => ({
        ...initialAddressData,
        ...getAddress,
        zipCode: prev.zipCode,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAddress]);

  useEffect(() => {
    if (getAddressError) {
      setAddressData((prev) => ({
        ...initialAddressData,
        zipCode: prev.zipCode,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAddressError]);

  useEffect(() => {
    if (createAddress) {
      setTabKey(
        onboarding.isOnboardingCaf || onboarding.useCelcoinOnboarding
          ? TAB_KEY.PIN
          : onboarding.useIdwall
          ? TAB_KEY.IDWALL_VERIFICATION
          : TAB_KEY.DOCUMENT
      );
      dispatch(OnboardingActions.cleanAddress());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createAddress,
    dispatch,
    onboarding.isOnboardingCaf,
    onboarding.useCelcoinOnboarding,
    onboarding.useIdwall,
  ]);

  useEffect(() => {
    if (
      saveUserDocument &&
      saveUserPhoto &&
      tabKey === TAB_KEY.DOCUMENT &&
      !onboarding.useIdwall
    ) {
      setTabKey(TAB_KEY.PIN);
    }

    if (
      saveUserDocument &&
      saveUserPhoto &&
      navigate?.location?.state?.accountType === TaxIdentifierType.PF &&
      isUserMinor &&
      onboarding.allowMinor &&
      (userDataForm.representants[0]?.type ===
        RepresentorType.LEGAL_REPRESENTOR ||
        (account?.representants &&
          account?.representants[0]?.type ===
            RepresentorType.LEGAL_REPRESENTOR))
    ) {
      setTabKey(TAB_KEY.PIN);

      return;
    }

    if (onboarding.useIdwall && saveUserPhoto) {
      setTabKey(TAB_KEY.DOCUMENT);
      return;
    }

    if (
      saveUserDocument &&
      saveUserPhoto &&
      navigate?.location?.state?.accountType === TaxIdentifierType.PF &&
      isUserMinor &&
      onboarding.allowMinor
    ) {
      setTabKey(TAB_KEY.MINOR_REPRESENTANTS);

      return;
    }

    if (
      saveUserDocument &&
      saveUserPhoto &&
      saveDocument !== undefined &&
      navigate?.location?.state?.accountType === TaxIdentifierType.PJ
    ) {
      if (onboarding.allowCnpjCard && cadastralSituation === null) {
        return setTabKey(TAB_KEY.PIN);
      }
      setTabKey(TAB_KEY.NEW_REPRESENTANTS);

      return;
    }

    if (tabKey === TAB_KEY.DOCUMENT) {
      setTabKey(TAB_KEY.PIN);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveUserDocument, saveUserPhoto, saveDocument, cadastralSituation]);

  useEffect(() => {
    if (settings?.customDocuments && settings?.customDocuments.length > 0) {
      if (
        onboarding.allowSocialContract &&
        (userDataForm?.account?.companyType === CompanyType.LTDA ||
          userDataForm?.account?.companyType === CompanyType.ME ||
          account?.companyType === CompanyType.LTDA ||
          account?.companyType === CompanyType.ME) &&
        !saveSocialContract
      ) {
        return;
      }

      if (
        onboarding.allowMinutesOfElection &&
        (userDataForm?.account?.companyType === CompanyType.SA ||
          account?.companyType === CompanyType.SA) &&
        !saveMinutesOfElection
      ) {
        return;
      }

      if (savePowerOfAttorney && saveDocument) {
        if (navigate?.location?.state?.isRetry) {
          setTabKey(TAB_KEY.FINAL);
        } else {
          setTabKey(TAB_KEY.NEW_REPRESENTANTS);
        }
      }
    } else {
      if (savePowerOfAttorney) {
        if (navigate?.location?.state?.isRetry) {
          setTabKey(TAB_KEY.FINAL);
        } else {
          setTabKey(TAB_KEY.NEW_REPRESENTANTS);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savePowerOfAttorney, saveDocument, saveDocument]);

  useEffect(() => {
    if (createPin) {
      setTabKey(TAB_KEY.FINAL);
    }
  }, [createPin]);

  useEffect(() => {
    if (navigate?.location?.state?.tab) {
      setTabKey(navigate?.location?.state?.tab ?? TAB_KEY.FIRST_STEP);
    }
  }, [navigate?.location?.state?.tab]);

  useEffect(() => {
    let isLogged = !!accessToken;

    if (!isLogged) {
      isLogged = !!StorageService.getItem('session-token');
    }
    if (!isLogged) {
      setTabKey(TAB_KEY.FIRST_STEP);
      if (navigate?.location?.state?.accountType) {
        navigate('/abertura-de-conta', {
          state: {
            accountType: navigate?.location?.state?.accountType,
            groupType: navigate?.location?.state?.groupType,
          },
        });
      } else {
        navigate('/');
      }
    } else if (account.status) {
      if (account.status === AccountStatus.PENDING_DOCUMENTS) {
        setTabKey(TAB_KEY.FINAL);

        return;
      }

      if (account.status === AccountStatus.PENDING_SOCIAL_CONTRACT) {
        setTabKey(TAB_KEY.DOCUMENT);

        return;
      }

      if (
        account.status === AccountStatus.REQUESTED_SELF &&
        onboarding.useIdwall
      ) {
        setTabKey(TAB_KEY.IDWALL_VERIFICATION);
        return;
      }

      const newTab = NavigationService.getOnboardingTab(account.status);
      setTabKey(newTab ?? TAB_KEY.FIRST_STEP);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, account.status, account.type, navigate]);

  return (
    <AdvancedCard testId="open-account-card">
      <div className="open-account">
        <PanelContentHeader
          pageTitle={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.PAGE_TITLE')}
          pageDescription={
            !onboarding.fullRepresentativePercent
              ? translate('PAGES.AUTH.REGISTER.ONBOARD.PF.PAGE_DESCRIPTION')
              : translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.PAGE_DESCRIPTION_REPRESENTATIVES'
                )
          }
        />
        {isLoading > 0 && <Loading />}
        <Tabs
          defaultActiveKey={TAB_KEY.FIRST_STEP}
          activeKey={tabKey}
          type="card"
        >
          <Tabs.TabPane
            tab={TabTitle(
              translate('PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.TAB_LABEL')
            )}
            key={TAB_KEY.FIRST_STEP}
            disabled
          >
            {navigate?.location?.state?.accountType === TaxIdentifierType.PF ? (
              <CreateAccountUser
                setIsValidMotherName={setIsValidMotherName}
                setIsValidName={setIsValidName}
                setUserDataForm={setUserDataForm}
                representantsIndex={representantsIndex}
                userData={userDataForm}
                onFormChange={onFormChange}
                allPercentage={allPercentage}
                taxIdentifierType={navigate?.location?.state?.accountType}
                setIsValidDate={setIsValidDate}
                setTabKey={setTabKey}
                tabKey={tabKey}
                handleFirstStepPerson={handleFirstStepPerson}
                setIsUserMinor={setIsUserMinor}
                isUserMinor={isUserMinor}
              />
            ) : (
              <CreateAccountCorporative
                setIsValidDate={setIsValidDate}
                userData={userDataForm}
                onFormChange={onFormChange}
                companyTypeValue={selectOptionsFormatter(
                  settings?.companyTypes
                )}
                businessLineValue={selectOptionsFormatter(
                  settings?.businessLine
                )}
                handleFirstStepCompany={handleFirstStepCompany}
              />
            )}
          </Tabs.TabPane>
          {!navigate?.location?.state?.isRetry && (
            <Tabs.TabPane
              tab={TabTitle(
                translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.TAB_LABEL'
                )
              )}
              key={TAB_KEY.APPROVAL_DEVICE}
              disabled
            >
              <ApprovalDevice
                onFormChange={onFormChange}
                userDataForm={userDataForm}
              />
            </Tabs.TabPane>
          )}
          {navigate?.location?.state?.accountType === TaxIdentifierType.PF &&
          !navigate?.location?.state?.isRetry &&
          onboarding.occupations ? (
            <Tabs.TabPane
              tab={TabTitle(
                translate('PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.TITLE')
              )}
              key={TAB_KEY.OCCUPATIONS}
              disabled
            >
              <OnboardingCbo
                setOccupationsData={setOccupationsData}
                occupationsData={occupationsData}
                setUserDataForm={setUserDataForm}
                userData={userDataForm}
              />
            </Tabs.TabPane>
          ) : null}
          {!navigate?.location?.state?.isRetry && onboarding.recommendation ? (
            <Tabs.TabPane
              tab={TabTitle(
                translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.RECOMMENDATION.TAB_LABEL'
                )
              )}
              key={TAB_KEY.RECOMMENDATION}
              disabled
            >
              <Recommendation onFormChange={onFormChange} />
            </Tabs.TabPane>
          ) : null}
          {navigate?.location?.state?.accountType === TaxIdentifierType.PJ && (
            <Tabs.TabPane
              tab={TabTitle(
                translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.REPRESENTANTS.TAB_LABEL'
                )
              )}
              key={TAB_KEY.REPRESENTANTS}
              disabled
            >
              <CreateAccountUser
                isPending={
                  !!navigate?.location?.state?.isRetry ||
                  (!!settings?.customDocuments &&
                    settings?.customDocuments.length > 0)
                }
                addressFields={onboarding.representativeAddress}
                tabKey={tabKey}
                setIsValidMotherName={setIsValidMotherName}
                setIsValidName={setIsValidName}
                setIsValidDate={setIsValidDate}
                setUserDataForm={setUserDataForm}
                representantsIndex={representantsIndex}
                userData={userDataForm}
                allPercentage={allPercentage}
                onFormChange={onFormChange}
                taxIdentifierType={navigate?.location?.state?.accountType}
                setTabKey={setTabKey}
                setIsUserMinor={setIsUserMinor}
                isUserMinor={isUserMinor}
                handleRepresentsValidation={handleRepresentsValidation}
              />
            </Tabs.TabPane>
          )}

          {navigate?.location?.state?.isRetry && sendTermsOfUseDataRetry ? (
            <>
              <Tabs.TabPane
                tab={TabTitle(
                  translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.TERMS_OF_USE.TAB_LABEL'
                  )
                )}
                key={TAB_KEY.TERMS_OF_USE}
                disabled
              >
                <TermsAndPrivacy
                  title={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.TERMS_OF_USE.FORM.LABEL'
                  )}
                  checkboxLabel={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.TERMS_OF_USE.FORM.ACCEPT'
                  )}
                  url={getTermByType(TermsTypes.TERMS_OF_USE).url ?? ''}
                  value={acceptTermsOfUse}
                  onChange={setAcceptTermsOfUse}
                />
              </Tabs.TabPane>

              {onboarding.privacyPolicy ? (
                <Tabs.TabPane
                  tab={TabTitle(
                    translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.PRIVACY_POLICY.TAB_LABEL'
                    )
                  )}
                  key={TAB_KEY.PRIVACY_POLICY}
                  disabled
                >
                  <TermsAndPrivacy
                    title={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.PRIVACY_POLICY.FORM.LABEL'
                    )}
                    checkboxLabel={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.PRIVACY_POLICY.FORM.ACCEPT'
                    )}
                    url={getTermByType(TermsTypes.PRIVACY_POLICY).url ?? ''}
                    value={acceptPrivacyPolicy}
                    onChange={setAcceptPrivacyPolicy}
                  />
                </Tabs.TabPane>
              ) : null}
            </>
          ) : null}

          {!navigate?.location?.state?.isRetry && (
            <>
              <Tabs.TabPane
                tab={TabTitle(
                  translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.TERMS_OF_USE.TAB_LABEL'
                  )
                )}
                key={TAB_KEY.TERMS_OF_USE}
                disabled
              >
                <TermsAndPrivacy
                  title={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.TERMS_OF_USE.FORM.LABEL'
                  )}
                  checkboxLabel={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.TERMS_OF_USE.FORM.ACCEPT'
                  )}
                  url={getTermByType(TermsTypes.TERMS_OF_USE).url ?? ''}
                  value={acceptTermsOfUse}
                  onChange={setAcceptTermsOfUse}
                />
              </Tabs.TabPane>
              {onboarding.privacyPolicy && !onboarding.isOnboardingCaf ? (
                <Tabs.TabPane
                  tab={TabTitle(
                    translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.PRIVACY_POLICY.TAB_LABEL'
                    )
                  )}
                  key={TAB_KEY.PRIVACY_POLICY}
                  disabled
                >
                  <TermsAndPrivacy
                    title={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.PRIVACY_POLICY.FORM.LABEL'
                    )}
                    checkboxLabel={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.PRIVACY_POLICY.FORM.ACCEPT'
                    )}
                    url={getTermByType(TermsTypes.PRIVACY_POLICY).url ?? ''}
                    value={acceptPrivacyPolicy}
                    onChange={setAcceptPrivacyPolicy}
                  />
                </Tabs.TabPane>
              ) : null}
              {onboarding.isOnboardingCaf ? (
                <Tabs.TabPane
                  tab={TabTitle(
                    translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.USER_PHOTO.TAB_LABEL'
                    )
                  )}
                  key={TAB_KEY.DOCUMENT_CAF}
                  disabled
                >
                  <OnboardingCAF
                    taxIdentifier={userDataForm.account?.taxIdentifier ?? ''}
                    phoneNumber={userDataForm.cellphone ?? ''}
                    email={userDataForm.account?.email ?? ''}
                    onOnboardingComplete={() => setTabKey(TAB_KEY.CREDENTIALS)}
                  />
                </Tabs.TabPane>
              ) : null}

              {onboarding.acceptanceOfTariffs ? (
                <Tabs.TabPane
                  tab={TabTitle(
                    translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ACCEPTANCE_OF_TARIFFS.TAB_LABEL'
                    )
                  )}
                  key={TAB_KEY.ACCEPTANCE_OF_TARIFFS}
                  disabled
                >
                  <TermsAndPrivacy
                    title={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ACCEPTANCE_OF_TARIFFS.FORM.LABEL'
                    )}
                    checkboxLabel={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ACCEPTANCE_OF_TARIFFS.FORM.ACCEPT'
                    )}
                    url={
                      getTermByType(TermsTypes.ACCEPTANCE_OF_TARIFFS).url ?? ''
                    }
                    value={acceptAcceptanceOfTarrifs}
                    onChange={setAcceptAcceptanceOfTarrifs}
                  />
                </Tabs.TabPane>
              ) : null}
              <Tabs.TabPane
                tab={TabTitle(
                  translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.CREDENTIALS.TAB_LABEL'
                  )
                )}
                key={TAB_KEY.CREDENTIALS}
                disabled
              >
                <CreateAccountCredential
                  userData={userDataForm}
                  onFormChange={onFormChange}
                  noLogin={noLogin}
                  selectedBullets={selectedBullets}
                  setSelectedBullets={setSelectedBullets}
                />
              </Tabs.TabPane>
            </>
          )}

          <Tabs.TabPane
            tab={TabTitle(
              navigate?.location?.state?.accountType === TaxIdentifierType.PF
                ? translate('PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.TAB_LABEL')
                : translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.TAB_LABEL_CNPJ'
                  )
            )}
            key={TAB_KEY.ADDRESS}
            disabled
          >
            <OnboardingAddress
              addressData={addressData}
              onFormChange={onFormChangeAddress}
              addressError={getAddressError}
            />
          </Tabs.TabPane>

          {!navigate?.location?.state?.isRetry && onboarding.useIdwall ? (
            <Tabs.TabPane
              tab={TabTitle(
                translate('PAGES.AUTH.REGISTER.ONBOARD.PF.IDWALL.TAB_LABEL')
              )}
              key={TAB_KEY.IDWALL_VERIFICATION}
              disabled
            >
              <div className="idwall_content">
                <IdwallSDK setToken={setIdwallSdkToken} />
              </div>
            </Tabs.TabPane>
          ) : null}

          {!onboarding.isOnboardingCaf &&
            !onboarding.useCelcoinOnboarding &&
            !navigate?.location?.state?.isRetry && (
              <Tabs.TabPane
                tab={TabTitle(
                  translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.USER_PHOTO.TAB_LABEL'
                  )
                )}
                key={TAB_KEY.DOCUMENT}
                disabled
              >
                {settings?.customDocuments.map((pendingDocument, index) => (
                  <CustomDocument
                    key={index}
                    setFileSelected={setFileSelectedDocumentCustom}
                    nameDocument={pendingDocument.name}
                  />
                ))}

                {navigate?.location?.state?.accountType ===
                TaxIdentifierType.PJ ? (
                  <>
                    {onboarding.allowCnpjCard ? (
                      <OnboardingCadastralSituationDocument
                        setFileSelected={setFileSelectedCadastralSituation}
                      />
                    ) : null}

                    {requiresPowerOfAttorney ? (
                      <ApprovalCompany
                        fromOnboarding={true}
                        setFileSelected={setFileSelectedDocument}
                      />
                    ) : null}

                    {onboarding.allowMinutesOfElection &&
                    userDataForm?.account?.companyType === CompanyType.SA ? (
                      <OnboardingMinutesOfElection
                        setFileSelected={setFileSelectedMinutesOfElection}
                      />
                    ) : null}

                    {requiresSocialContract ? (
                      <OnboardingSocialContract
                        setFileSelected={setSelectedSocialContract}
                      />
                    ) : null}

                    {requiresCompanyStatute ? (
                      <OnboardingCompanyStatute
                        setFileSelected={setSelectedCompanyStatute}
                      />
                    ) : null}

                    {requiresDeclarationBeneficiaries ? (
                      <OnboardingDeclarationBeneficiaries
                        setFileSelected={setSelectedDeclarationBeneficiaries}
                      />
                    ) : null}

                    {!onboarding.useIdwall && (
                      <OnboardingDocumentPhoto
                        documentType={documentType}
                        setDocumentType={setDocumentType}
                        setFileSelectedDigitalCnh={setFileSelectedDigitalCnh}
                        setFileSelectedCnhBack={setFileSelectedCnhBack}
                        setFileSelectedCnh={setFileSelectedCnh}
                        setFileSelectedRgFront={setFileSelectedRgFront}
                        setFileSelectedRgBack={setFileSelectedRgBack}
                      />
                    )}

                    {onboarding.allowFileOfMembersAndAdministrator ? (
                      <OnboardingFileOfMembersAndAdministrator
                        setFileSelected={
                          setFileSelectedFileOfMembersAndAdministrator
                        }
                      />
                    ) : null}

                    {!onboarding.useIdwall && (
                      <OnboardingUserPhoto setFileSelected={setFileSelected} />
                    )}

                    {onboarding.allowBusinessAddressDocument ? (
                      <OnboardingBusinessAddressDocument
                        setFileSelected={setFileSelectedBusinessAddressDocument}
                      />
                    ) : null}

                    {onboarding.allowHomeAddressDocument ? (
                      <OnboardingHomeAddressDocument
                        setFileSelected={setFileSelectedHomeAddressDocument}
                      />
                    ) : null}

                    {onboarding.companyAnnualIncomeDocument ? (
                      <OnboardingAnnualIncome
                        setFileSelected={setFileAnnualIncome}
                      />
                    ) : null}

                    {onboarding.allowProofOfInvoicing ? (
                      <OnboardingProofOfInvoicing
                        setFileSelected={setFileSelectedProofOfInvoicing}
                      />
                    ) : null}

                    {onboarding.allowFinancialStatements ? (
                      <OnboardingFinancialStatements
                        setFileSelected={setFileSelectedFinancialStatements}
                      />
                    ) : null}

                    {onboarding.companyAnnualIncomeDocument ? (
                      <OnboardingAnnualIncome
                        setFileSelected={setFileAnnualIncome}
                      />
                    ) : null}

                    {onboarding.allowProofOfInvoicing ? (
                      <OnboardingProofOfInvoicing
                        setFileSelected={setFileSelectedProofOfInvoicing}
                      />
                    ) : null}

                    {onboarding.allowFinancialStatements ? (
                      <OnboardingFinancialStatements
                        setFileSelected={setFileSelectedFinancialStatements}
                      />
                    ) : null}

                    {onboarding.allowOtherDocuments ? (
                      <OnboardingHomeAddressDocument
                        setFileSelected={setFileSelectedOtherDocuments}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    {!onboarding.useIdwall && (
                      <>
                        <OnboardingDocumentPhoto
                          documentType={documentType}
                          setDocumentType={setDocumentType}
                          setFileSelectedDigitalCnh={setFileSelectedDigitalCnh}
                          setFileSelectedCnh={setFileSelectedCnh}
                          setFileSelectedCnhBack={setFileSelectedCnhBack}
                          setFileSelectedRgFront={setFileSelectedRgFront}
                          setFileSelectedRgBack={setFileSelectedRgBack}
                          isUserMinor={isUserMinor}
                        />

                        <OnboardingUserPhoto
                          setFileSelected={setFileSelected}
                        />
                      </>
                    )}

                    {onboarding.allowOtherDocuments ? (
                      <OnboardingHomeAddressDocument
                        setFileSelected={setFileSelectedOtherDocuments}
                      />
                    ) : null}
                  </>
                )}
                <h4 className="open-account__document-advice">
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.DOCUMENT.DOCUMENT_ADVICE'
                  )}
                </h4>
              </Tabs.TabPane>
            )}

          {navigate?.location?.state?.accountType === TaxIdentifierType.PF &&
          onboarding.allowMinor &&
          isUserMinor ? (
            <Tabs.TabPane
              tab={TabTitle(
                translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.NEW_REPRESENTANT.TAB_LABEL'
                )
              )}
              key={TAB_KEY.MINOR_REPRESENTANTS}
              disabled
            >
              <CreateAccountUser
                isPending={
                  !!navigate?.location?.state?.isRetry ||
                  (!!settings?.customDocuments &&
                    settings?.customDocuments.length > 0)
                }
                tabKey={tabKey}
                setIsValidMotherName={setIsValidMotherName}
                setIsValidName={setIsValidName}
                setIsValidDate={setIsValidDate}
                setUserDataForm={setUserDataForm}
                representantsIndex={representantsIndex}
                userData={userDataForm}
                allPercentage={allPercentage}
                onFormChange={onFormChange}
                taxIdentifierType={navigate?.location?.state?.accountType}
                setTabKey={setTabKey}
              />
            </Tabs.TabPane>
          ) : null}
          {navigate?.location?.state?.accountType === TaxIdentifierType.PF &&
          !navigate?.location?.state?.isRetry &&
          onboarding.allowMinor &&
          isUserMinor ? (
            <Tabs.TabPane
              tab={TabTitle(
                translate('PAGES.AUTH.REGISTER.ONBOARD.PF.USER_PHOTO.TAB_LABEL')
              )}
              key={TAB_KEY.LEGAL_REPRESENTANTS_DOCUMENT}
              disabled
            >
              {settings?.customDocuments.map((pendingDocument, index) => (
                <CustomDocument
                  key={index}
                  setFileSelected={setFileSelectedDocumentCustom}
                  nameDocument={pendingDocument.name}
                />
              ))}
              <OnboardingDocumentPhoto
                documentType={documentType}
                setDocumentType={setDocumentType}
                setFileSelectedDigitalCnh={setFileSelectedDigitalCnh}
                setFileSelectedCnh={setFileSelectedCnh}
                setFileSelectedRgFront={setFileSelectedRgFront}
                setFileSelectedRgBack={setFileSelectedRgBack}
              />
              <OnboardingUserPhoto setFileSelected={setFileSelected} />
            </Tabs.TabPane>
          ) : null}
          {!navigate?.location?.state?.isRetry && (
            <Tabs.TabPane
              tab={TabTitle(
                translate('PAGES.AUTH.REGISTER.ONBOARD.PF.PIN.TAB_LABEL')
              )}
              key={TAB_KEY.PIN}
              disabled
            >
              <OnboardingCreatePin code={pin} setCode={setPin} />
            </Tabs.TabPane>
          )}
          {!navigate?.location?.state?.isRetry && (
            <Tabs.TabPane
              tab={TabTitle(
                translate('PAGES.AUTH.REGISTER.ONBOARD.PF.FINAL.TAB_LABEL')
              )}
              key={TAB_KEY.FINAL}
              disabled
            >
              <OnboardingFinal />
            </Tabs.TabPane>
          )}
        </Tabs>

        {tabKey === TAB_KEY.APPROVAL_DEVICE && AdvanceApprovalDeviceStep}
        {tabKey === TAB_KEY.OCCUPATIONS && OccupationDeviceStep}
        {tabKey === TAB_KEY.RECOMMENDATION && RecommendationDeviceStep}
        {tabKey === TAB_KEY.TERMS_OF_USE && AdvanceTermsOfUse}
        {tabKey === TAB_KEY.PRIVACY_POLICY && AdvancePrivacyPolicy}
        {tabKey === TAB_KEY.ACCEPTANCE_OF_TARIFFS && advanceAcceptanceOfTariffs}
        {tabKey === TAB_KEY.CREDENTIALS && AdvanceCredentials}
        {tabKey === TAB_KEY.ADDRESS && AdvanceAddress}
        {(tabKey === TAB_KEY.DOCUMENT ||
          tabKey === TAB_KEY.LEGAL_REPRESENTANTS_DOCUMENT) &&
          AdvanceDocument}
        {tabKey === TAB_KEY.PIN && AdvancePin}
        {tabKey === TAB_KEY.FINAL && AdvanceFinal}
        {tabKey === TAB_KEY.IDWALL_VERIFICATION && AdvanceIdwall}
      </div>
      <MessageModal
        open={showMessage}
        setVisible={setShowMessage}
        buttonOkText="Ok"
        onConfirm={() => setShowMessage(false)}
        title={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.MESSAGE_MODAL.TITLE')}
        message={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.MESSAGE_MODAL.MESSAGE'
        )}
      />
      <MessageModal
        open={showMessageRecommendation}
        setVisible={setShowRecommendation}
        buttonOkText="Ok"
        onConfirm={() => setShowRecommendation(false)}
        title={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.RECOMMENDATION.CODE_INVALID'
        )}
        message={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.RECOMMENDATION.TEXT'
        )}
      />
    </AdvancedCard>
  );
};

export default OpenAccount;
